import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiInfo } from 'react-icons/fi';
import whiteOwlLogo from '../assets/LOGO WHITE.png';
import buddyMascotGif from '../assets/NewHome/buddy-mascot.gif';
import { APP_CONFIG } from '../config/envConfig';
import SignInModal from './SignInModal';
import { openRazorpayCheckout } from '../utils/razorpayCheckout';
import { trackCtaClicked } from '../utils/mixpanelClient';

// Served from /public so CRA's SVGR does not parse Canva namespace tags
const buddyMorningBrief = '/NewHome/morning_brief.svg';
const buddyMarketResearch = '/NewHome/market_research_and_pipeline_review.svg';
const buddyPremeetingPrep = '/NewHome/pre-meeting_preparation.svg';
const buddyDiscoveryCalls = '/NewHome/discovery_calls.svg';
const buddyPitchPresentation = '/NewHome/pitch_and_presentation.svg';
const buddyIndustryEvents = '/NewHome/industry_events_%26_networking.svg';
const buddySignoffPrep = '/NewHome/sign_off_%26_prep_for_tomorrow.svg';

const ASK_QUESTIONS = [
  'Who should I follow up with before Friday?',
  'Which accounts are showing buying intent?',
  'What changes at my top ten clients this week?',
];

// ------------------------------------------------------------------
// NewHome — full page build, ported from the approved static HTML/CSS
// reference. All rules below are scoped under .wo-newhome so nothing
// leaks into the rest of the (Bricolage Grotesque / Tailwind) site.
// ------------------------------------------------------------------

const STYLES = `
.wo-newhome{
  --teal-deep:#122E86;
  --teal:#427BF8;
  --teal-soft:#A9C4FD;
  --cream:#FAFBFF;
  --orange:#E8A136;
  --ink:#17224A;
  --line:rgba(250,251,255,.20);
}
.wo-newhome *{margin:0;padding:0;box-sizing:border-box}
.wo-newhome{scroll-behavior:smooth}
.wo-newhome{font-family:'Instrument Sans',sans-serif;background:var(--cream);color:var(--ink);line-height:1.6}
.wo-newhome nav{position:fixed;top:0;left:0;right:0;z-index:50;display:flex;align-items:center;justify-content:space-between;padding:20px 48px;background:linear-gradient(180deg,rgba(12,22,48,.85),rgba(12,22,48,0));backdrop-filter:blur(6px)}
.wo-newhome .logo{display:flex;align-items:center;gap:10px;color:#EFF3FB;font-weight:600;font-size:1.05rem;letter-spacing:.02em}
.wo-newhome .logo svg{width:34px;height:34px}
.wo-newhome .logo img{height:56px;width:auto}
.wo-newhome .nav-links{display:flex;gap:28px;align-items:center}
.wo-newhome .nav-links a{color:#EFF3FB;text-decoration:none;font-size:.92rem;opacity:.85}
.wo-newhome .nav-links a:hover{opacity:1}
.wo-newhome .nav-toggle{display:none}
.wo-newhome .btn{display:inline-block;padding:11px 22px;border-radius:999px;font-family:inherit;font-weight:600;font-size:.92rem;text-decoration:none;cursor:pointer;border:none;transition:transform .15s ease}
.wo-newhome .btn:hover{transform:translateY(-1px)}
.wo-newhome .btn-orange{background:linear-gradient(135deg,#E8862E,#F5A94B);color:#17224A;box-shadow:0 4px 18px rgba(232,134,46,.45)}
.wo-newhome .btn-ghost{background:transparent;color:#EFF3FB;border:1px solid rgba(250,250,251,.35)}
.wo-newhome .hero{min-height:100vh;display:flex;align-items:center;background:linear-gradient(180deg,#060D22 0%,#0C1630 42%,#1B2E59 82%,#27407A 115%);color:#EFF3FB;padding:100px 48px 48px;position:relative;overflow:hidden}
.wo-newhome .hero-stars{position:absolute;inset:0;pointer-events:none;opacity:.75;background-image:radial-gradient(1.2px 1.2px at 8% 14%,rgba(255,255,255,.5) 50%,transparent 51%),radial-gradient(1px 1px at 16% 42%,rgba(255,255,255,.35) 50%,transparent 51%),radial-gradient(1.4px 1.4px at 24% 78%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1px 1px at 38% 18%,rgba(255,255,255,.45) 50%,transparent 51%),radial-gradient(1.2px 1.2px at 47% 62%,rgba(255,255,255,.3) 50%,transparent 51%),radial-gradient(1px 1px at 58% 28%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1.5px 1.5px at 67% 8%,rgba(255,255,255,.5) 50%,transparent 51%),radial-gradient(1px 1px at 74% 54%,rgba(255,255,255,.32) 50%,transparent 51%),radial-gradient(1.2px 1.2px at 82% 36%,rgba(255,255,255,.45) 50%,transparent 51%),radial-gradient(1px 1px at 88% 72%,rgba(255,255,255,.35) 50%,transparent 51%),radial-gradient(1.3px 1.3px at 93% 22%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1px 1px at 6% 88%,rgba(255,255,255,.28) 50%,transparent 51%),radial-gradient(1.1px 1.1px at 33% 48%,rgba(255,255,255,.38) 50%,transparent 51%),radial-gradient(1px 1px at 52% 90%,rgba(255,255,255,.3) 50%,transparent 51%),radial-gradient(1.4px 1.4px at 96% 48%,rgba(255,255,255,.42) 50%,transparent 51%)}
.wo-newhome .hero-inner{position:relative;z-index:10;width:100%;max-width:1200px;margin:0 auto}
.wo-newhome .hero-copy{display:flex;flex-direction:column;align-items:center;text-align:center;max-width:900px;margin:0 auto;position:relative;z-index:10}
.wo-newhome .mascot-wrap{position:relative;margin-top:48px;margin-bottom:8px;animation:wo-drift-in .9s ease-out both}
.wo-newhome .mascot{display:block;height:200px;width:auto;animation:wo-bob 3s ease-in-out infinite, wo-sway 6s ease-in-out infinite;filter:drop-shadow(0 12px 26px rgba(34,11,109,.25))}
.wo-newhome .mascot-wrap:hover .mascot{animation:wo-bob 1.2s ease-in-out infinite, wo-sway 2s ease-in-out infinite}
.wo-newhome .mascot-shadow{width:90px;height:14px;background:rgba(0,0,0,.28);border-radius:50%;margin:2px auto 0;filter:blur(5px);animation:wo-shadow 3s ease-in-out infinite}
@keyframes wo-bob{0%,100%{translate:0 0}50%{translate:0 -18px}}
@keyframes wo-sway{0%,100%{rotate:-2.5deg}50%{rotate:2.5deg}}
@keyframes wo-shadow{0%,100%{transform:scaleX(1);opacity:.3}50%{transform:scaleX(.7);opacity:.14}}
@keyframes wo-drift-in{from{opacity:0;transform:translateY(-24px)}to{opacity:1;transform:translateY(0)}}
@media (prefers-reduced-motion:reduce){.wo-newhome .mascot,.wo-newhome .mascot-shadow,.wo-newhome .mascot-wrap{animation:none!important}}
.wo-newhome .badge{position:relative;margin:6px 0 18px;padding:11px 26px;border-radius:999px;background:linear-gradient(135deg,#E8862E,#F5A94B);color:#17224A;font-weight:700;font-size:1.05rem;letter-spacing:.02em;box-shadow:0 6px 24px rgba(232,134,46,.5)}
.wo-newhome .eyebrow{font-size:.78rem;letter-spacing:.22em;text-transform:uppercase;color:#F5A94B;margin-bottom:14px;position:relative}
.wo-newhome h1{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(2rem,3.5vw,3.25rem);line-height:1.14;max-width:none;position:relative}
.wo-newhome h1 em{font-style:italic;color:#F5A94B;white-space:nowrap}
.wo-newhome h1 .h1-line{display:block;white-space:nowrap}
.wo-newhome .hero .sub{margin:16px 0 26px;max-width:48ch;font-size:1.05rem;color:rgba(250,250,251,.85);position:relative}
.wo-newhome .hero-cta{padding:14px 34px;font-size:1.02rem;white-space:nowrap}
.wo-newhome .hero-cta.btn-orange{background:linear-gradient(90deg,#f5a33a,#fbc865);box-shadow:0 4px 18px rgba(245,163,58,.45)}
.wo-newhome .trial-box{position:relative;display:flex;align-items:center;justify-content:space-between;gap:24px;width:100%;max-width:756px;padding:22px 28px;border:1px solid rgba(250,251,255,.18);border-radius:16px;background:rgba(250,251,255,.03);text-align:left}
.wo-newhome .trial-box-copy strong{display:block;font-size:1.05rem;color:#EFF3FB;margin-bottom:6px}
.wo-newhome .trial-box-copy p{font-size:.92rem;color:rgba(239,243,251,.65);max-width:42ch}
@media (max-width:640px){.wo-newhome .trial-box{flex-direction:column;align-items:flex-start;text-align:left}.wo-newhome .trial-box .hero-cta{width:100%;text-align:center}}
.wo-newhome .hero-ask{width:100%;margin-top:32px;padding-top:24px;border-top:1px solid rgba(250,251,255,.14)}
.wo-newhome .hero-ask .eyebrow{color:var(--orange);margin-bottom:10px}
.wo-newhome .hero-ask-question{font-family:'Fraunces',serif;font-style:italic;font-size:clamp(1.1rem,2.1vw,1.5rem);color:#FAFBFF;min-height:1.4em}
.wo-newhome .hero-ask-cursor{display:inline-block;width:2px;height:1em;background:var(--orange);margin-left:3px;vertical-align:-0.15em;animation:wo-cursor-blink 1s step-end infinite}
@keyframes wo-cursor-blink{0%,49%{opacity:1}50%,100%{opacity:0}}
@media (prefers-reduced-motion:reduce){.wo-newhome .hero-ask-cursor{animation:none}}
.wo-newhome .tagline{margin-top:34px;font-family:'Fraunces',serif;font-style:italic;font-size:1.05rem;color:#F5A94B;position:relative}
.wo-newhome .whatis{padding:88px 24px;max-width:1140px;margin:0 auto}
.wo-newhome .whatis-grid{display:grid;grid-template-columns:1fr 1.05fr;gap:48px;align-items:center}
.wo-newhome .whatis h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.8rem,3.4vw,2.6rem);color:var(--teal-deep);margin-bottom:20px}
.wo-newhome .whatis-copy p{font-size:1.02rem;color:#4E5B7E;margin-bottom:16px;max-width:48ch}
.wo-newhome .whatis-copy p em{font-style:italic;font-weight:600;color:inherit}
.wo-newhome .whatis-copy p:last-child{margin-bottom:0;font-weight:600;color:#17224A}
.wo-newhome .whatis-video{position:relative;width:100%;aspect-ratio:16/9;border-radius:18px;overflow:hidden;background:#0C1630;box-shadow:0 16px 44px rgba(23,34,74,.14);border:1px solid #DFE7FB}
.wo-newhome .whatis-video iframe{position:absolute;inset:0;width:100%;height:100%;border:0}
@media (max-width:860px){.wo-newhome .whatis{padding:64px 24px}.wo-newhome .whatis-grid{grid-template-columns:1fr;gap:28px}}
.wo-newhome .quotes{padding:90px 24px 70px;max-width:1140px;margin:0 auto}
.wo-newhome .quotes h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.6rem,3vw,2.2rem);text-align:center;margin-bottom:48px}
.wo-newhome .qgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.wo-newhome .qcard{background:#fff;border:1px solid #DFE7FB;border-radius:18px;padding:28px 26px;margin:0;display:flex;flex-direction:column}
.wo-newhome .qcard blockquote{font-size:.95rem;color:#4E5B7E;font-style:italic;flex:1;margin-bottom:18px;line-height:1.65}
.wo-newhome .qcard figcaption{display:flex;align-items:center;gap:12px}
.wo-newhome .qavatar{width:46px;height:46px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,#0C1630,#1B2E59);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:.9rem;object-fit:cover}
.wo-newhome .qcard figcaption strong{display:block;font-size:.95rem;color:#17224A}
.wo-newhome .qcard figcaption div span{font-size:.8rem;color:#8593B5}
@media (max-width:820px){.wo-newhome .qgrid{grid-template-columns:1fr}}
.wo-newhome .day{padding:0px 24px;max-width:1140px;margin:0 auto;display:flex;flex-direction:column;align-items:center}
.wo-newhome .day .stack{width:100%}
.wo-newhome .day .badge{margin-bottom:26px}
.wo-newhome .day h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.8rem,3.4vw,2.6rem);text-align:center;margin-bottom:14px}
.wo-newhome .day .lede{text-align:center;max-width:520px;margin:0 auto 70px;color:#4E5B7E}
.wo-newhome .stack{position:relative}
.wo-newhome .panel{position:sticky;top:calc(84px + var(--off,0px));margin-bottom:56px;background:#fff;border:1px solid #DFE7FB;border-radius:20px;overflow:hidden;box-shadow:0 -10px 34px rgba(30,58,79,.12);transform-origin:top center;transition:transform .35s ease,filter .35s ease;will-change:transform;padding:0}
.wo-newhome .panel[data-i="0"]{--off:0px}
.wo-newhome .panel[data-i="1"]{--off:56px}
.wo-newhome .panel[data-i="2"]{--off:112px}
.wo-newhome .panel[data-i="3"]{--off:168px}
.wo-newhome .panel[data-i="4"]{--off:168px}
.wo-newhome .panel[data-i="5"]{--off:168px}
.wo-newhome .panel[data-i="6"]{--off:168px}
.wo-newhome .panel.behind{transform:scale(.985);filter:brightness(.96)}
.wo-newhome .panel.behind .p-body{opacity:.25}
.wo-newhome .p-head{display:flex;align-items:center;justify-content:space-between;height:56px;padding:0 28px;background:linear-gradient(90deg,#EEF3FE,#FAFBFF);border-bottom:1px solid #DFE7FB;flex-shrink:0}
.wo-newhome .p-time{font-family:'Fraunces',serif;font-weight:700;font-size:1.25rem;color:#E8862E}
.wo-newhome .p-body{padding:8px 20px 14px;transition:opacity .35s ease;display:grid;grid-template-columns:.9fr 1.25fr;gap:20px;align-items:center;max-height:calc(100vh - var(--off,0px) - 84px - 56px - 24px);overflow:hidden}
.wo-newhome .p-media{--shrink:clamp(0.4,calc((100vh - var(--off,0px) - 186px) / 230px),1);width:calc(100% * var(--shrink));height:calc(230px * var(--shrink));min-height:0;justify-self:center;border-radius:14px;background:linear-gradient(135deg,#EEF3FE,#F7FAFF);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:#6E82B4;font-size:.85rem;font-weight:600;overflow:hidden}
.wo-newhome .p-media svg{width:34px;height:34px;opacity:.7}
.wo-newhome .p-media img,.wo-newhome .p-media video{width:100%;height:100%;object-fit:cover;border-radius:14px}
.wo-newhome .panel h3{font-size:1.25rem;color:var(--teal-deep);margin-bottom:8px}
.wo-newhome .panel p{font-size:1rem;color:#4E5B7E;max-width:56ch}
.wo-newhome .who{display:inline-block;font-size:.72rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;padding:5px 14px;border-radius:999px}
.wo-newhome .who.buddy{background:rgba(66,123,248,.12);color:#2F5FE8}
.wo-newhome .who.you{background:rgba(232,134,46,.18);color:#B4611B}
@media (max-width:760px){
  .wo-newhome .p-body{padding:18px 20px 22px;grid-template-columns:1fr;gap:16px;overflow-y:auto}
  .wo-newhome .p-media{min-height:0;aspect-ratio:16/10}
  .wo-newhome .p-head{padding:10px 20px}
  .wo-newhome .p-time{font-size:1.05rem}
}
@media (prefers-reduced-motion:reduce){.wo-newhome .panel{position:static;transform:none!important;filter:none!important}.wo-newhome .panel .p-body{opacity:1!important;max-height:none!important;overflow:visible!important}}
.wo-newhome .daytag{margin-top:16px;font-family:'Fraunces',serif;font-style:italic;font-size:1.3rem;color:#E8862E;text-align:center}
.wo-newhome .steps{padding:12px 24px 100px;max-width:1080px;margin:0 auto}
.wo-newhome .steps h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.8rem,3.4vw,2.6rem);text-align:center;margin-bottom:14px}
.wo-newhome .hnum{font-size:1.35em;color:#E8862E;font-weight:700}
.wo-newhome .steps .lede{text-align:center;max-width:520px;margin:0 auto 64px;color:#4E5B7E}
.wo-newhome .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
.wo-newhome .card{background:#fff;border-radius:22px;padding:36px 30px;border:1px solid #DFE7FB;position:relative}
.wo-newhome .card .num{font-family:'Fraunces',serif;font-size:1.5rem;font-weight:700;background:linear-gradient(135deg,#E8862E,#F5A94B);width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:16px;box-shadow:0 6px 16px rgba(232,134,46,.4);color:#17224A}
.wo-newhome .card h3{font-size:1.15rem;margin-bottom:10px;color:var(--teal-deep)}
.wo-newhome .card p{font-size:.95rem;color:#4E5B7E}
.wo-newhome .trust{padding:64px 24px;max-width:900px;margin:0 auto;text-align:center;border-bottom:1px solid #DFE7FB}
.wo-newhome .trust h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.8rem,3.4vw,2.6rem);margin-bottom:14px}
.wo-newhome .trust .lede{max-width:560px;margin:0 auto 40px;color:#4E5B7E}
.wo-newhome .trust-tag{font-family:'Fraunces',serif;font-style:italic;font-size:1.25rem;color:#17224A;margin-bottom:30px}
.wo-newhome .trust-badges{display:flex;justify-content:center;gap:22px;flex-wrap:wrap}
.wo-newhome .builtby{margin-top:34px;font-size:.92rem;color:#4E5B7E;max-width:560px;margin-left:auto;margin-right:auto}
.wo-newhome .tbadge{display:flex;align-items:center;gap:16px;background:#fff;border:1px solid #DFE7FB;border-radius:16px;padding:18px 24px;text-align:left;box-shadow:0 6px 20px rgba(23,34,74,.06);min-width:250px}
.wo-newhome .tlogo{width:52px;height:52px;flex-shrink:0}
.wo-newhome .tbadge strong{display:block;font-size:1.02rem;color:#17224A;margin-bottom:2px}
.wo-newhome .tbadge span:last-child{font-size:.8rem;color:#8593B5}
.wo-newhome .ptoggles{display:flex;justify-content:center;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:44px}
.wo-newhome .cursel{position:relative}
.wo-newhome .cursel select{appearance:none;-webkit-appearance:none;border:1px solid #DFE7FB;background:#F5F8FE;border-radius:10px;padding:9px 34px 9px 14px;font-family:'Instrument Sans',sans-serif;font-size:.85rem;font-weight:600;color:#17224A;cursor:pointer}
.wo-newhome .cursel::after{content:'\\25BE';position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#8593B5;pointer-events:none;font-size:.8rem}
.wo-newhome .tg{display:inline-flex;background:#fff;border:1px solid #DFE7FB;border-radius:999px;padding:4px}
.wo-newhome .tgbtn{border:none;background:transparent;padding:8px 16px;border-radius:999px;font-family:'Instrument Sans',sans-serif;font-size:.85rem;font-weight:600;color:#4E5B7E;cursor:pointer}
.wo-newhome .tgbtn em{font-style:normal;font-size:.72rem;color:#E8862E;margin-left:2px}
.wo-newhome .tgbtn.on{background:linear-gradient(135deg,#E8862E,#F5A94B);color:#17224A}
.wo-newhome .tgbtn.on em{color:#17224A}
.wo-newhome .bilnote{font-size:.72rem;color:#8593B5;margin:2px 0 0}
.wo-newhome .pricing{padding:70px 24px;max-width:1140px;margin:0 auto}
.wo-newhome .pricing h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.8rem,3.4vw,2.6rem);text-align:center;margin-bottom:14px}
.wo-newhome .pricing .lede{text-align:center;max-width:560px;margin:0 auto 64px;color:#4E5B7E}
.wo-newhome .pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;align-items:stretch;width:100%;margin:0 auto}
.wo-newhome .pcard{background:#fff;border:1px solid #DFE7FB;border-radius:20px;padding:30px 26px;display:flex;flex-direction:column;position:relative}
.wo-newhome .pcard.hot{border:2px solid #E8862E;box-shadow:0 16px 44px rgba(232,134,46,.22);transform:scale(1.03)}
.wo-newhome .pop{position:absolute;top:-13px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,#E8862E,#F5A94B);color:#fff;font-size:.72rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:5px 14px;border-radius:999px;white-space:nowrap}
.wo-newhome .pcard h3{font-size:1.05rem;color:var(--teal-deep);margin-bottom:10px}
.wo-newhome .price{font-family:'Fraunces',serif;font-weight:700;font-size:2.2rem;color:var(--teal-deep)}
.wo-newhome .price span{font-size:1rem;font-weight:400;color:#8593B5}
.wo-newhome .seats{font-size:.85rem;color:#8593B5;margin:10px 0 18px}
.wo-newhome .pcard ul{list-style:none;margin:0 0 26px;flex:1}
.wo-newhome .pcard li{font-size:.9rem;color:#4E5B7E;padding:7px 0 7px 24px;position:relative}
.wo-newhome .pcard li::before{content:'\\2713';position:absolute;left:0;color:#E8862E;font-weight:700}
.wo-newhome .fkeep{white-space:nowrap}
.wo-newhome .finfo{position:relative;display:inline-flex;vertical-align:middle;margin-left:5px}
.wo-newhome .ficon-btn{background:none;border:none;padding:0;margin:0;display:inline-flex;cursor:help;color:#8593B5;line-height:0}
.wo-newhome .ficon-btn:hover,.wo-newhome .ficon-btn:focus-visible{color:#2F5FE8}
.wo-newhome .ficon-btn svg{width:13px;height:13px}
.wo-newhome .ftip{position:absolute;left:50%;bottom:calc(100% + 8px);transform:translate(-50%,4px);background:#17224A;color:#fff;font-size:.74rem;line-height:1.45;padding:9px 11px;border-radius:8px;width:210px;text-align:left;opacity:0;visibility:hidden;transition:opacity .15s ease,transform .15s ease;z-index:20;box-shadow:0 8px 20px rgba(23,34,74,.25);pointer-events:none}
.wo-newhome .ftip::after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);border:5px solid transparent;border-top-color:#17224A}
.wo-newhome .finfo:hover .ftip,.wo-newhome .finfo:focus-within .ftip{opacity:1;visibility:visible;transform:translate(-50%,0)}
.wo-newhome .btn-line{background:transparent;color:var(--teal-deep);border:1.5px solid #C9D8FB;text-align:center}
.wo-newhome .btn-line:hover{border-color:#E8862E;color:#B4611B}
.wo-newhome .pcard .btn{width:100%;text-align:center}
.wo-newhome .fine{text-align:center;font-size:.8rem;color:#8593B5;margin-top:28px}
.wo-newhome .pricing-error{text-align:center;font-size:.9rem;color:#C0392B;margin:0 auto 20px;max-width:520px}
@media (max-width:860px){.wo-newhome .pgrid{grid-template-columns:repeat(2,1fr)}.wo-newhome .pcard.hot{transform:none}}
@media (max-width:560px){.wo-newhome .pgrid{grid-template-columns:1fr}}
.wo-newhome .cta-band{position:relative;overflow:hidden;background:radial-gradient(900px 500px at 50% -10%,#1B2E59 0%,#0C1630 60%);color:#FAFBFF;text-align:center;padding:80px 24px}
.wo-newhome .cta-band h2,.wo-newhome .cta-band p,.wo-newhome .cta-band .btn{position:relative}
.wo-newhome .cta-band h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.6rem,3vw,2.3rem);margin-bottom:12px}
.wo-newhome .cta-band p{opacity:.8;margin-bottom:32px}
.wo-newhome .trial-cta{text-align:center;padding:80px 24px 40px;max-width:640px;margin:0 auto;border-top:1px solid #DFE7FB}
.wo-newhome .trial-cta h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.6rem,3vw,2.2rem);margin-bottom:10px}
.wo-newhome .trial-cta p{color:#4E5B7E;margin-bottom:28px}
.wo-newhome .faq{padding:40px 24px 90px;max-width:760px;margin:0 auto}
.wo-newhome .faq h2{font-family:'Fraunces',serif;font-weight:600;font-size:clamp(1.6rem,3vw,2.2rem);text-align:center;margin-bottom:44px}
.wo-newhome .faqs details{border-bottom:1px solid #DFE7FB;padding:18px 4px}
.wo-newhome .faqs summary{font-weight:600;color:#17224A;cursor:pointer;font-size:1rem;list-style:none;position:relative;padding-right:28px}
.wo-newhome .faqs summary::-webkit-details-marker{display:none}
.wo-newhome .faqs summary::after{content:'+';position:absolute;right:2px;color:#E8862E;font-size:1.3rem;line-height:1}
.wo-newhome .faqs details[open] summary::after{content:'\\2212'}
.wo-newhome .faqs p{margin-top:10px;font-size:.93rem;color:#4E5B7E}
.wo-newhome .faqs a{color:#2B5CE0}
.wo-newhome footer{background:#FFFFFF;color:#4E5B7E;padding:28px 48px;border-top:1px solid #DFE7FB;display:flex;align-items:center;justify-content:space-between;font-size:.85rem;opacity:.75}
.wo-newhome footer a{color:#2B5CE0}
.wo-newhome footer .footer-right{display:flex;align-items:center;gap:24px}
.wo-newhome footer .footer-links{display:flex;align-items:center;gap:20px}
.wo-newhome footer .footer-socials{display:flex;align-items:center;gap:14px}
.wo-newhome footer .footer-socials img{height:18px;width:18px}
@media (max-width:960px){
  .wo-newhome .hero{padding:92px 24px 40px}
}
@media (max-width:900px){
  .wo-newhome h1 .h1-line{white-space:normal}
}
@media (max-width:820px){
  .wo-newhome nav{padding:14px 20px;gap:12px}
  .wo-newhome .logo img{height:40px}
  .wo-newhome .nav-links{gap:10px}
  .wo-newhome .nav-links a:not(.btn-orange){display:none}
  .wo-newhome .nav-links .btn{padding:9px 16px;font-size:.85rem}
  .wo-newhome .nav-toggle{display:flex;flex-direction:column;justify-content:center;align-items:center;gap:5px;width:36px;height:36px;flex-shrink:0;background:transparent;border:none;cursor:pointer;padding:0}
  .wo-newhome .nav-toggle span{display:block;width:20px;height:2px;background:#EFF3FB;border-radius:2px;transition:transform .2s ease,opacity .2s ease}
  .wo-newhome .nav-toggle[aria-expanded="true"] span:nth-child(1){transform:translateY(7px) rotate(45deg)}
  .wo-newhome .nav-toggle[aria-expanded="true"] span:nth-child(2){opacity:0}
  .wo-newhome .nav-toggle[aria-expanded="true"] span:nth-child(3){transform:translateY(-7px) rotate(-45deg)}
  .wo-newhome .mobile-menu{position:absolute;top:100%;left:0;right:0;display:flex;flex-direction:column;background:#0C1630;border-top:1px solid rgba(250,251,255,.14);padding:6px 20px 12px;box-shadow:0 16px 30px rgba(6,13,34,.4)}
  .wo-newhome .mobile-menu a{color:#EFF3FB;text-decoration:none;font-size:.95rem;padding:13px 2px;border-bottom:1px solid rgba(250,251,255,.08)}
  .wo-newhome .mobile-menu a:last-child{border-bottom:none}
  .wo-newhome .grid{grid-template-columns:1fr}
  .wo-newhome footer{flex-direction:column;gap:12px;text-align:center}
  .wo-newhome footer .footer-right{flex-direction:column;gap:12px}
}
.wo-newhome .btn:focus-visible{outline:2px solid var(--teal-soft);outline-offset:2px}
.wo-newhome .wo-toast{
  position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:100;
  max-width:min(520px,calc(100vw - 32px));padding:14px 18px;border-radius:12px;
  background:#E8F8EF;color:#166534;border:1px solid #BBF7D0;font-size:.92rem;line-height:1.45;text-align:center;
  box-shadow:0 12px 40px rgba(22,101,52,.12);animation:wo-toast-in .25s ease;
}
@keyframes wo-toast-in{from{opacity:0;transform:translate(-50%,-8px)}to{opacity:1;transform:translate(-50%,0)}}
`;

const DAY_PANELS = [
  { time: '8:00 AM', who: 'buddy', whoLabel: 'Buddy', title: 'Morning Brief', text: "Wake up to what's changed overnight — a leadership move at a target account, a funding signal in your market, a shift in a key stakeholder's role. Start the day informed, not catching up.", media: buddyMorningBrief },
  { time: '9:00 AM', who: 'buddy', whoLabel: 'Buddy', title: 'Market Research and Pipeline Review', text: 'Buddy flags fresh trigger events: a Board Seat change, a PE Entry, a Phase 2 Gap, across your pipeline overnight. Spot the opportunities worth chasing, see where deals are stalling, and walk into the day with your workflow already prioritised.', media: buddyMarketResearch },
  { time: '11:00 AM', who: 'buddy', whoLabel: 'Buddy', title: 'Pre-meeting Preparation', text: "Before you dial in, Buddy's done the digging, stakeholder map, financials, open questions, talking points that actually matter to this client. A pre-meeting brief, ready before you are.", media: buddyPremeetingPrep },
  { time: '12:00–2:00 PM', who: 'buddy', whoLabel: 'Buddy', title: 'Discovery Calls', text: 'Buddy sits in, capturing commitments and open questions as they happen, mapped against everything already known about the account. You stay present in the room; nothing said gets lost by Friday.', media: buddyDiscoveryCalls },
  { time: '3:00 PM', who: 'buddy', whoLabel: 'Buddy', title: 'Pitch and Presentation', text: 'Buddy turns the meeting into momentum: a post-call summary, an updated account file, a first-draft SoW, and follow-ups ready to send — so the next move happens same-day, not next week.', media: buddyPitchPresentation },
  { time: '4:00 PM', who: 'buddy', whoLabel: 'Buddy', title: 'Industry Events & Networking', text: "Walking into a room full of strangers? Buddy's already mapped who's worth meeting — attendee profiles, keynote speaker intel, and the Alumni Signal that turns small talk into a real conversation.", media: buddyIndustryEvents },
  { time: '5:00 PM–8:00 AM', who: 'buddy', whoLabel: 'Buddy', title: 'Sign-Off & Prep for Tomorrow', text: "You clock out. Buddy doesn't — it's quietly watching your accounts and people overnight, lining up tomorrow's next actions, so nothing slips through while you're offline.", media: buddySignoffPrep },
];

const STEPS = [
  { title: 'Auto Setup', text: "Point Buddy at your website, upload decks or documents. It builds your firm's knowledge base and ideal-client profile in minutes, not weeks." },
  { title: 'Scans for Growth Signals', text: 'Buddy monitors your current and past clients, people, events, meetings and market to surface buying signals that are most likely to convert. It works like your 24x7 BD director.' },
  { title: 'Learns Continuously', text: "Every interaction makes Buddy sharper: better signals, better drafts, better calls. Within days, it feels like a team member who's been with you for years." },
];

const PLAN_FEATURES = {
  Boutique: [
    { label: 'Geography: 3' },
    { label: 'Auto Watchlist: 100', tooltip: 'Automatically monitors your selected accounts and people.' },
    { label: 'Auto Signal Tracker: 10', tooltip: 'Automated monitoring rules that continuously track trigger events for your target accounts.' },
    { label: 'Import Limit: 100 accounts,', keep: '300 people', tooltip: 'Maximum number of accounts and people you can import.' },
    { label: '5,000 credits/mo', tooltip: 'Monthly credits for AI-powered tasks (signal generation, account & people intelligence, research and more).' },
  ],
  Growth: [
    { label: 'Geography: 5' },
    { label: 'Auto Watchlist: 200', tooltip: 'Automatically monitors your selected accounts and people.' },
    { label: 'Auto Signal Tracker: 25', tooltip: 'Automated monitoring rules that continuously track trigger events for your target accounts.' },
    { label: 'Import Limit: 200 accounts,', keep: '600 people', tooltip: 'Maximum number of accounts and people you can import.' },
    { label: '10,000 credits/mo', tooltip: 'Monthly credits for AI-powered tasks (signal generation, account & people intelligence, research and more).' },
  ],
  Scale: [
    { label: 'Geography: 10' },
    { label: 'Auto Watchlist: 300', tooltip: 'Automatically monitors your selected accounts and people.' },
    { label: 'Auto Signal Tracker: 50', tooltip: 'Automated monitoring rules that continuously track trigger events for your target accounts.' },
    { label: 'Import Limit: 500 accounts,', keep: '1,500 people', tooltip: 'Maximum number of accounts and people you can import.' },
    { label: '25,000 credits/mo', tooltip: 'Monthly credits for AI-powered tasks (signal generation, account & people intelligence, research and more).' },
  ],
};

const PLAN_META = {
  Boutique: { name: 'Boutique', seats: '2 users', hot: false, features: PLAN_FEATURES.Boutique },
  Growth: { name: 'Growth', seats: '5 users', hot: true, features: PLAN_FEATURES.Growth },
  Scale: { name: 'Scale', seats: '10 users', hot: false, features: PLAN_FEATURES.Scale },
};

const PLANS = {
  m: [
    { ...PLAN_META.Boutique, gbp: 99, usd: 129, inr: 12999, id: { gbp: 17, usd: 23, inr: 20 } },
    { ...PLAN_META.Growth, gbp: 249, usd: 319, inr: 32999, id: { gbp: 18, usd: 24, inr: 21 } },
    { ...PLAN_META.Scale, gbp: 449, usd: 569, inr: 59999, id: { gbp: 19, usd: 25, inr: 22 } },
  ],
  q: [
    { ...PLAN_META.Boutique, gbp: 267, usd: 348, inr: 35097, id: { gbp: 29, usd: 26, inr: 32 } },
    { ...PLAN_META.Growth, gbp: 672, usd: 861, inr: 89097, id: { gbp: 30, usd: 27, inr: 33 } },
    { ...PLAN_META.Scale, gbp: 1212, usd: 1536, inr: 161997, id: { gbp: 31, usd: 28, inr: 34 } },
  ],
  a: [
    { ...PLAN_META.Boutique, gbp: 950, usd: 1238, inr: 124790, id: { gbp: 38, usd: 41, inr: 35 } },
    { ...PLAN_META.Growth, gbp: 2390, usd: 3062, inr: 316790, id: { gbp: 39, usd: 42, inr: 36 } },
    { ...PLAN_META.Scale, gbp: 4310, usd: 5462, inr: 575990, id: { gbp: 40, usd: 43, inr: 37 } },
  ],
};

const FAQS = [
  { q: 'What does WhiteOwl actually do?', a: 'WhiteOwl is an AI growth platform that automates the consulting sales cycle, helping you keep a watch on market signals, spot new deals, track your proposals, keep an eye on past and current clients, prep for events — and more.' },
  { q: 'Who is Buddy?', a: 'Buddy is your AI growth companion inside WhiteOwl. It proactively monitors your accounts, surfaces important signals, prepares meeting briefs, and helps you complete day-to-day sales and business development tasks.' },
  { q: 'How are WhiteOwl and Buddy different?', a: 'WhiteOwl is the platform. Buddy is the AI companion that works within WhiteOwl. Think of WhiteOwl as your complete workspace for sales and business development, while Buddy is the smart assistant that helps you get the most out of the platform with simple prompts.' },
  { q: 'Can I try WhiteOwl for free?', a: 'Yes. WhiteOwl offers a 2-week free trial, allowing you to explore the platform, experience Buddy, and see how it can support your sales and business development workflow before committing to a paid plan.' },
  { q: 'Does it replace consultants or sales teams?', a: 'No. WhiteOwl is an AI assistant for consultants. It augments their work by cutting down repetitive tasks. It helps you gather sales intelligence, research information, and create winning proposals. WhiteOwl becomes your partner in sales success by accelerating closures and winning more deals.' },
  { q: "Is my client's data safe?", a: 'Absolutely. WhiteOwl utilises enterprise encryption and APIs that adhere to global data privacy standards. We are an ISO 27001 company. Your data is used only for the purpose you need; there is no unauthorised access, and your information remains safe and confidential.' },
  { q: 'Is WhiteOwl a CRM?', a: 'No. WhiteOwl is not a CRM. It works alongside your existing CRM and helps you spot new deals based on your past work, track the right people at priority accounts, monitor past and current clients, prepare for events, and more.' },
  { q: 'Will my work or my client data be used to train the AI?', a: <>No. Your data is never shared or reused to train our global AI models or across our other client accounts. WhiteOwl only uses your data to personalise and improve your experience. For example, remembering context, tailoring briefs, and refining proposals for your account. Everything in your login stays with you. For more details, you can read our <Link to="/privacy">privacy policy</Link>.</> },
  { q: 'How does WhiteOwl ensure accuracy and avoid hallucination?', a: "WhiteOwl draws signals and insights based on the consulting firm's business offerings, industries, and the type of client you are catering to. The platform gives you the option to add/remove your target geographies, clients, industries, target persona and target segment on which it generates triggers. These triggers are further customisable. Based on the provided inputs, it crawls trusted web sources to bring you the most relevant sales signals. We utilise a combination of technical strategies, data practices, and controls to maximise accuracy and avoid hallucinations. Whether it's a signal about funding, leadership changes, mergers and acquisitions, or hiring, you can check the exact source of the information by clicking on it." },
];

const CUR_SYMBOL = { gbp: '£', usd: '$', inr: '₹' };
const BIL_SUFFIX = { m: '/mo', q: '/qtr', a: '/yr' };
const BIL_NOTE = { m: '', q: 'billed quarterly', a: 'billed annually' };
const IMPX = { m: '2', q: '3', a: '5' };
const EXTRA_USER_PRICE = { gbp: 49, usd: 69, inr: 6700 };
const ADDON_CREDIT_PACKS = [
  { credits: 2500, price: { gbp: 50, usd: 70, inr: 6800 } },
  { credits: 5000, price: { gbp: 100, usd: 132, inr: 12740 } },
];

function formatAmount(n, cur) {
  const rounded = Math.round(n);
  return cur === 'inr' ? rounded.toLocaleString('en-IN') : rounded.toLocaleString('en-US');
}

const MediaPlaceholder = () => (
  <div className="p-media">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M3 17l5-4 4 3 4-4 5 5" />
    </svg>
    <span>Image / GIF</span>
  </div>
);

const OwlMascot = ({ className }) => (
  <img src={buddyMascotGif} alt="Buddy the owl mascot" className={className} />
);

const PRICING_OAUTH_ERRORS = {
  account_exists: 'Account with this email already exists. Please use another email.',
  user_already_exist: 'Account with this email already exists. Please use another email.',
  invalid_plan: 'That plan is invalid or unavailable. Please choose another plan.',
  payment_link_failed: 'Unable to start checkout. Please try again.',
};

const SIGNUP_OAUTH_ERRORS = {
  personal_email_not_allowed: 'Please use your work email. Personal email addresses are not allowed.',
  account_exists: 'Account with this email already exists. Please use another email.',
  user_already_exist: 'Account with this email already exists. Please use another email.',
  oauth_failed: 'Sign-up failed. Please try again.',
};

const resolveAuthErrorMessage = (code) =>
  SIGNUP_OAUTH_ERRORS[code]
  || PRICING_OAUTH_ERRORS[code]
  || 'Something went wrong during sign-up. Please try again.';

const NewHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [signInOpen, setSignInOpen] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [buyPlanId, setBuyPlanId] = useState(null);
  const [pricingError, setPricingError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const openSignIn = (ctaLabel, position) => {
    if (ctaLabel) trackCtaClicked(ctaLabel, { position });
    setBuyPlanId(null);
    setSubmittedQuery('');
    setSignInOpen(true);
  };

  const openBuyNow = (planId, ctaLabel, position) => {
    if (ctaLabel) trackCtaClicked(ctaLabel, { position });
    setSubmittedQuery('');
    setBuyPlanId(planId);
    setPricingError('');
    setSignInOpen(true);
  };

  // OAuth / signup returns here:
  // - /?order_id=...&amount=...&currency=...&key=...&email=... (buy-now via OAuth — opens Razorpay Checkout.js)
  // - /pricing?payment_link=...|/pricing?error=...
  // - /?error=...
  // Frontend checks for `error` first, per the backend contract.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('error');

    if (err) {
      const isPricingCheckoutError =
        location.pathname === '/pricing' && Boolean(PRICING_OAUTH_ERRORS[err]);

      if (isPricingCheckoutError) {
        setPricingError(PRICING_OAUTH_ERRORS[err]);
        navigate(
          { pathname: location.pathname, search: '', hash: 'pricing' },
          { replace: true }
        );
        requestAnimationFrame(() => {
          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }

      setToastMessage(resolveAuthErrorMessage(err));
      navigate({ pathname: '/', search: '' }, { replace: true });
      return;
    }

    const orderId = params.get('order_id');
    if (orderId) {
      const amount = params.get('amount');
      const currency = params.get('currency') || 'INR';
      const key = params.get('key');
      const email = params.get('email') || '';

      // Clear the query string right away so this doesn't re-fire or linger in the URL bar.
      navigate({ pathname: location.pathname, search: '' }, { replace: true });

      if (!amount || !key) {
        setToastMessage('Unable to start checkout. Please try again.');
        return;
      }

      openRazorpayCheckout({
        orderId,
        amount,
        currency,
        key,
        email,
        navigate,
        onError: (message) => setToastMessage(message || 'Something went wrong. Please try again.'),
      });
      return;
    }

    const paymentLink = params.get('payment_link');
    if (paymentLink) {
      // Leave this site and open the Razorpay URL on its own (not as a query param).
      const url = /^https?:\/\//i.test(paymentLink) ? paymentLink : `https://${paymentLink}`;
      window.location.replace(url);
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const id = setTimeout(() => setToastMessage(''), 6000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  // ---- Day timeline sticky-stack "behind" effect ----
  const panelRefs = useRef([]);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const updateStack = () => {
      const panels = panelRefs.current.filter(Boolean);
      panels.forEach((p, i) => {
        const next = panels[i + 1];
        if (!next) {
          p.classList.remove('behind');
          return;
        }
        const nr = next.getBoundingClientRect();
        const pr = p.getBoundingClientRect();
        if (nr.top < pr.top + pr.height * 0.5) p.classList.add('behind');
        else p.classList.remove('behind');
      });
    };
    window.addEventListener('scroll', updateStack, { passive: true });
    window.addEventListener('resize', updateStack);
    updateStack();
    return () => {
      window.removeEventListener('scroll', updateStack);
      window.removeEventListener('resize', updateStack);
    };
  }, []);

  // ---- Hero "consulting partners ask" typewriter ----
  const [askIndex, setAskIndex] = useState(0);
  const [askText, setAskText] = useState('');
  const [askPhase, setAskPhase] = useState('typing');
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setAskText(ASK_QUESTIONS[0]);
      return undefined;
    }
    const current = ASK_QUESTIONS[askIndex];
    let timeout;
    if (askPhase === 'typing') {
      if (askText.length < current.length) {
        timeout = setTimeout(() => setAskText(current.slice(0, askText.length + 1)), 45);
      } else {
        timeout = setTimeout(() => setAskPhase('deleting'), 1800);
      }
    } else if (askText.length > 0) {
      timeout = setTimeout(() => setAskText(current.slice(0, askText.length - 1)), 25);
    } else {
      timeout = setTimeout(() => {
        setAskIndex((i) => (i + 1) % ASK_QUESTIONS.length);
        setAskPhase('typing');
      }, 200);
    }
    return () => clearTimeout(timeout);
  }, [askText, askPhase, askIndex]);

  // ---- Pricing currency / billing toggle ----
  const [currency, setCurrency] = useState('gbp');
  const [billing, setBilling] = useState('m');

  useEffect(() => {
    const plans = PLANS[billing].map((plan) => ({
      name: plan.name,
      billing,
      currency,
      amount: plan[currency],
      planId: plan.id[currency],
      id: plan.id,
    }));
    console.log('plans', plans);
    console.log('PLANS', PLANS);
  }, [billing, currency]);

  return (
    <div className="wo-newhome">
      <style>{STYLES}</style>
      {toastMessage ? (
        <div className="wo-toast" role="status" aria-live="polite">
          {toastMessage}
        </div>
      ) : null}

      <nav>
        <div className="logo">
          <img src={whiteOwlLogo} alt="WhiteOwl" />
        </div>
        <div className="nav-links">
          <a href="#steps">How it works</a>
          <a href="#pricing">Pricing</a>
          <a href="/blog">Blog</a>
          <a href={APP_CONFIG.appUrl} className="btn btn-ghost">Sign in</a>
          <a href="#pricing" className="btn btn-orange">Buy now</a>
        </div>
        <button
          type="button"
          className="nav-toggle"
          aria-label="Toggle menu"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        {mobileNavOpen ? (
          <div className="mobile-menu" role="menu">
            <a href="#steps" onClick={() => setMobileNavOpen(false)}>How it works</a>
            <a href="#pricing" onClick={() => setMobileNavOpen(false)}>Pricing</a>
            <a href="/blog" onClick={() => setMobileNavOpen(false)}>Blog</a>
            <a href={APP_CONFIG.appUrl} onClick={() => setMobileNavOpen(false)}>Sign in</a>
          </div>
        ) : null}
      </nav>

      <section className="hero" id="top">
        <div className="hero-stars" aria-hidden="true" />

        <div className="hero-inner">
          <div className="hero-copy">
            <div className="eyebrow">AI prepares. You decide.</div>
            <h1>
              <span className="h1-line">The AI <em>Growth Platform</em> for Consulting</span>
              <span className="h1-line">&amp; Professional Services Firms</span>
            </h1>
            <p className="sub">Turn your firm's relationships into pipeline. Watch market signals, spot new deals, track proposals, and walk into every meeting prepared.</p>
            <div className="trial-box">
              <div className="trial-box-copy">
                <strong>Try WhiteOwl free for 2 weeks</strong>
                <p>Connect your website, get your first signals the same day. No credit card.</p>
              </div>
              <button type="button" className="btn btn-orange hero-cta" onClick={() => openSignIn('Start free trial', 'hero')}>Start free trial</button>
            </div>

            <div className="hero-ask">
              <div className="eyebrow">Consulting partners ask WhiteOwl</div>
              <p className="hero-ask-question">
                {askText}
                <span className="hero-ask-cursor" aria-hidden="true" />
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="whatis" id="what-is">
        <div className="whatis-grid">
          <div className="whatis-copy">
            <h2>What is WhiteOwl AI?</h2>
            <p>WhiteOwl is an <em>AI growth platform</em> for consulting and professional services firms, an extension of your BD and sales team that gives you actionable deal and relationship intelligence.</p>
            <p>In practice, that means surfacing new lead signals, tracking the right people at priority accounts, keeping tabs on past and current clients, and prepping you for events and key meetings.</p>
            <p>One platform to power your growth engine and sales pipeline.</p>
          </div>
          <div className="whatis-video">
            <iframe
              src="https://www.youtube.com/embed/2ngn_tyjPOs?si=kbslsV0Ci0P9zVDu"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="day" id="day">
        <div className="mascot-wrap">
          <OwlMascot className="mascot" />
          <div className="mascot-shadow" />
        </div>
        <div className="badge">Your Growth Companion. 24×7</div>
        <h2>Simplify Your Day with Buddy</h2>
        <p className="lede">Keep your growth engine running around the clock, even when you are away.</p>
        <div className="stack">
          {DAY_PANELS.map((panel, i) => (
            <div
              className="panel"
              data-i={i}
              key={panel.time + panel.title}
              ref={(el) => { panelRefs.current[i] = el; }}
            >
              <div className="p-head">
                <span className="p-time">{panel.time}</span>
              </div>
              <div className="p-body">
                <div className="p-text">
                  <h3>{panel.title}</h3>
                  <p>{panel.text}</p>
                </div>
                {panel.media ? (
                  <div className="p-media">
                    <img src={panel.media} alt={panel.title} loading="lazy" />
                  </div>
                ) : (
                  <MediaPlaceholder />
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="daytag">Don't worry. Buddy's there.</p>
      </section>

      <section className="steps" id="steps">
        <h2><span className="hnum">3</span> Things that Make Buddy Unique</h2>
        <p className="lede">Not just another tool to manage, a companion that sets itself up, thinks like a BD director, and gets better with every signal.</p>
        <div className="grid">
          {STEPS.map((step, i) => (
            <div className="card" key={step.title}>
              <div className="num">{i + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <h2>Every deal starts with a signal. Don't miss yours.</h2>
        <p>Join the consulting firms already using Buddy to build pipeline faster. Start your free trial today.</p>
        <a onClick={() => openSignIn('Start free with Buddy', 'cta-band')} className="btn btn-orange">Start free with Buddy</a>
      </section>

      <section className="trust">
        <h2>Enterprise-grade Protection</h2>
        <p className="lede">WhiteOwl follows the highest industry standards for security and compliance.</p>
        <div className="trust-badges">
          <div className="tbadge">
            <svg className="tlogo" viewBox="0 0 48 48">
              <path d="M24 3l16 6v12c0 10.5-6.8 19.4-16 24C14.8 40.4 8 31.5 8 21V9z" fill="#1636A8" />
              <path d="M24 3l16 6v12c0 10.5-6.8 19.4-16 24z" fill="#2B5CE0" />
              <text x="24" y="22" textAnchor="middle" fontFamily="Instrument Sans,Arial" fontSize="8.5" fontWeight="700" fill="#fff">ISO</text>
              <text x="24" y="32" textAnchor="middle" fontFamily="Instrument Sans,Arial" fontSize="8" fontWeight="700" fill="#F5A94B">27001</text>
            </svg>
            <div><strong>ISO 27001</strong><span>Information Security certified</span></div>
          </div>
          <div className="tbadge">
            <svg className="tlogo" viewBox="0 0 48 48">
              <path d="M24 5c5 0 9.3 2.9 11.4 7.1A10 10 0 0 1 36 32H13a9 9 0 0 1-2.4-17.7A12 12 0 0 1 24 5z" fill="#E8F0FE" stroke="#4285F4" strokeWidth="2.4" />
              <path d="M17 24.5l5 5 9-10" fill="none" stroke="#34A853" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="37.5" cy="36.5" r="7.5" fill="#4285F4" />
              <path d="M37.5 32.5v5.5m0 0l2.6-2m-2.6 2l-2.6-2" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" />
            </svg>
            <div><strong>Google CASA</strong><span>Cloud security approved</span></div>
          </div>
          <div className="tbadge">
            <svg className="tlogo" viewBox="0 0 48 48">
              <rect x="10" y="20" width="28" height="21" rx="5" fill="#1636A8" />
              <path d="M16 20v-4a8 8 0 0 1 16 0v4" fill="none" stroke="#2B5CE0" strokeWidth="4" />
              <circle cx="24" cy="29.5" r="3.2" fill="#F5A94B" />
              <rect x="22.7" y="31" width="2.6" height="6" rx="1.3" fill="#F5A94B" />
            </svg>
            <div><strong>Per-firm isolation</strong><span>Your data stays yours, never shared across clients</span></div>
          </div>
        </div>
        <p className="builtby">Built on 30+ years of B2B sales and consulting experience, including organisations like PwC and IBM, and engineered by a dedicated AI team.</p>
      </section>

      <section className="pricing" id="pricing">
        <h2>Simple Pricing. Start Using Buddy Free.</h2>
        <p className="lede">Unlock growth with every query. No card required. Upgrade when WhiteOwl earns it.</p>
        <div className="ptoggles">
          <div className="tg">
            {[['m', 'Monthly', null], ['q', 'Quarterly', '−10%'], ['a', 'Annual', '−20%']].map(([key, label, off]) => (
              <button
                key={key}
                type="button"
                className={`tgbtn${billing === key ? ' on' : ''}`}
                onClick={() => setBilling(key)}
              >
                {label} {off && <em>{off}</em>}
              </button>
            ))}
          </div>
          <label className="cursel">
            <select aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option value="gbp">£ GBP</option>
              <option value="usd">$ USD</option>
              <option value="inr">₹ INR</option>
            </select>
          </label>
        </div>

        {pricingError ? <p className="pricing-error">{pricingError}</p> : null}

        <div className="pgrid">
          {PLANS[billing].map((plan) => {
            const amount = plan[currency];
            const planId = plan.id[currency];
            return (
              <div className={`pcard${plan.hot ? ' hot' : ''}`} key={plan.name}>
                {plan.hot && <div className="pop">Most popular</div>}
                <h3>{plan.name}</h3>
                <div className="price">
                  <span className="amt">{CUR_SYMBOL[currency]}{formatAmount(amount, currency)}</span>
                  <span>{BIL_SUFFIX[billing]}</span>
                </div>
                {BIL_NOTE[billing] && <div className="bilnote">{BIL_NOTE[billing]}</div>}
                <div className="seats">{plan.seats}</div>
                <ul>
                  {plan.features.map((f, idx) => {
                    const label = typeof f === 'string' ? f : f.label;
                    const keep = typeof f === 'string' ? null : f.keep;
                    const tooltip = typeof f === 'string' ? null : f.tooltip;
                    const info = tooltip && (
                      <span className="finfo">
                        <button type="button" className="ficon-btn" aria-label={tooltip}>
                          <FiInfo />
                        </button>
                        <span className="ftip">{tooltip}</span>
                      </span>
                    );
                    return (
                      <li key={idx}>
                        {label}
                        {keep ? (
                          <>
                            {' '}
                            <span className="fkeep">{keep}{info}</span>
                          </>
                        ) : info}
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  className={`btn ${plan.hot ? 'btn-orange' : 'btn-line'}`}
                  onClick={() => openBuyNow(planId, 'Buy now', 'pricing')}
                  disabled={!planId}
                >
                  Buy now
                </button>
              </div>
            );
          })}
        </div>
        <p className="fine">
          All plans include <strong>{IMPX[billing]}×</strong> account &amp; people import — bring your full book, point Buddy at what matters.
          {' '}Unused query credits carry forward 1 month on all plans. Additional users: {CUR_SYMBOL[currency]}{formatAmount(EXTRA_USER_PRICE[currency], currency)}/user/mo with 2000 credits.
          {' '}Add-on query credits: {ADDON_CREDIT_PACKS.map((pack) => `${CUR_SYMBOL[currency]}${formatAmount(pack.price[currency], currency)} → ${pack.credits.toLocaleString('en-GB')}`).join(' · ')}. Prices exclude VAT/GST.
        </p>
      </section>

      <section className="trial-cta">
        <h2>Try WhiteOwl free for 2 weeks</h2>
        <p>No credit card required. Ask anything related to growth — see what WhiteOwl finds in your market.</p>
        <a className="btn btn-orange" onClick={() => openSignIn('Try free', 'trial-cta')}>Try free</a>
      </section>

      <section className="faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faqs">
          {FAQS.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer>
        <span>© {new Date().getFullYear()} thewhiteowl. All rights reserved.</span>
        <span className="footer-right">
          <span className="footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of use</Link>
          </span>
          <span className="footer-socials">
            <a href="https://www.linkedin.com/company/whiteowlai/" target="_blank" rel="noopener noreferrer">
              <img src="/linkedin.svg" alt="LinkedIn" />
            </a>
            <a href="https://x.com/whiteowl_ai" target="_blank" rel="noopener noreferrer">
              <img src="/twitter.svg" alt="Twitter" />
            </a>
            <a href="https://www.instagram.com/thewhiteowlai/" target="_blank" rel="noopener noreferrer">
              <img src="/instagram.svg" alt="Instagram" />
            </a>
            <a href="https://www.youtube.com/@whiteowlai" target="_blank" rel="noopener noreferrer">
              <img src="/youtube.svg" alt="YouTube" />
            </a>
          </span>
        </span>
      </footer>

      <SignInModal
        open={signInOpen}
        onClose={() => {
          setSignInOpen(false);
          setBuyPlanId(null);
        }}
        query={submittedQuery}
        planId={buyPlanId}
      />
    </div>
  );
};

export default NewHome;
